<template>
  <div style="text-align:left;">
    <TopNav />
    <div class="column-left">
      <SideNav />
    </div>
  
    <div class="column-right">
      <!-- Ensure admin_datos is loaded before checking superadmin -->
      <div v-if="admin_datos && admin_datos.superadmin" class="main-container">
        <h2>Conversaciones Chatbot WhatsApp</h2>
        <hr>
  
        <!-- Filter Section -->
        <div class="filter-section">
          <label for="telefonoFilter">Filtrar por Teléfono:</label>
          <input type="text" id="telefonoFilter" v-model="filterTelefono" @keyup.enter="applyFilter" placeholder="Ingrese teléfono y presione Enter">
          <button @click="applyFilter" :disabled="loading">Filtrar</button>
          <button @click="clearFilter" :disabled="loading || !filterTelefono" style="margin-left: 5px;">Limpiar</button>
        </div>
  
        <!-- Loading Indicator -->
        <div v-if="loading" class="loading-indicator">
          Cargando conversaciones...
        </div>
  
        <!-- Error Message -->
        <div v-if="error" class="error-message">
          {{ error }}
        </div>
  
        <!-- Data Table -->
        <div v-if="!loading && !error && conversations.length > 0" class="data-table-container">
          <!-- Use template for multiple root elements per conversation -->
          <table class="conversation-table">
            <thead>
              <tr>
                <th><!-- Expander Icon --></th>
                <th>ID Conv.</th>
                <th>Teléfono</th>
                <th>Último Mensaje</th>
                <th>Nº Msgs</th> <!-- NEW Column -->
                <th>Estado Conv.</th>
                <th>Completa</th>
                <th>Thread ID</th>
                <th>Solicitudes Asociadas</th>
              </tr>
            </thead>
            <template v-for="conv in conversations">
              <!-- Main Conversation Row (Clickable) -->
              <tr :key="conv.id" @click="toggleRow(conv.id)" class="conversation-row" :class="{ 'expanded': isRowExpanded(conv.id) }">
                <td>
                  <span class="expander-icon">{{ isRowExpanded(conv.id) ? '▼' : '▶' }}</span>
                </td>
                <td>{{ conv.id }}</td>
                <td>{{ conv.telefono || 'N/A' }}</td>
                <td>{{ formatDate(conv.ultimo_mensaje) }}</td>
                <td>{{ conv.message_count }}</td> <!-- Display Message Count -->
                <td>{{ formatEstado(conv.estado) }}</td>
                <td>
                  <span :class="{'status-yes': conv.is_complete, 'status-no': !conv.is_complete}">
                    {{ conv.is_complete ? 'Sí' : 'No' }}
                  </span>
                </td>
                <td>{{ conv.thread_id || 'N/A' }}</td>
                <td class="solicitudes-cell">
                  <!-- Solicitudes display logic -->
                   <div v-if="conv.solicitudes_info && conv.solicitudes_info.length > 0">
                       <div v-for="(sol, index) in getVisibleSolicitudes(conv)" :key="sol.id" class="solicitud-item" :class="{ 'mt-10': index > 0 }">
                           <!-- Solicitud details -->
                           <div><strong>ID Sol:</strong> {{ sol.id }}</div>
                           <div><strong>Fecha:</strong> {{ formatDate(sol.fecha_creado) }}</div>
                           <div><strong>Estado:</strong> {{ formatSolicitudEstado(sol.estado) }}</div>
                           <div v-if="sol.utm_source"><strong>UTM Source:</strong> {{ sol.utm_source }}</div>
                           <div v-if="sol.utm_campaign"><strong>UTM Campaign:</strong> {{ sol.utm_campaign }}</div>
                       </div>
                       <!-- Show More/Less Button for Solicitudes -->
                       <button
                         v-if="conv.solicitudes_info.length > 2"
                         @click.stop="toggleSolicitudes(conv.id)" 
                         class="show-more-less-btn"
                       >
                         {{ isSolicitudesExpanded(conv.id) ? 'Mostrar menos' : `Mostrar ${conv.solicitudes_info.length - 2} más...` }}
                       </button>
                   </div>
                   <div v-else>
                       <span class="status-no">No</span> <!-- Indicate no associated solicitudes -->
                   </div>
                </td>
              </tr>
              <!-- Expanded Row for Messages (Conditionally Rendered) -->
              <tr v-if="isRowExpanded(conv.id)" :key="`${conv.id}-messages`" class="messages-row">
                <td :colspan="9"> <!-- Colspan to match number of columns -->
                  <div class="messages-container">
                    <div v-if="loadingMessages[conv.id]" class="loading-indicator">
                      Cargando mensajes...
                    </div>
                    <div v-else-if="errorMessages[conv.id]" class="error-message">
                      Error al cargar mensajes: {{ errorMessages[conv.id] }}
                    </div>
                    <div v-else-if="fetchedMessages[conv.id] && fetchedMessages[conv.id].length > 0">
                      <ul class="message-list">
                        <li v-for="msg in fetchedMessages[conv.id]" :key="msg.id" :class="['message-item', `message-${msg.origen}`]">
                          <div class="message-header">
                             <strong class="message-origin">{{ msg.origen === 'usuario' ? 'Usuario' : 'Bot' }}</strong>
                             <span class="message-timestamp">{{ formatDate(msg.fecha_creado, true) }}</span> <!-- Optional: full timestamp -->
                          </div>
                          <div class="message-content">
                            <pre>{{ msg.contenido }}</pre> <!-- Use pre for formatting -->
                          </div>
                        </li>
                      </ul>
                    </div>
                    <div v-else>
                      No hay mensajes para mostrar en esta conversación.
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </table>
        </div>
  
         <!-- No Data Message -->
        <div v-if="!loading && !error && conversations.length === 0">
          No se encontraron conversaciones con los filtros aplicados.
        </div>
  
        <!-- Pagination -->
        <div v-if="!loading && !error && pagination && pagination.total_pages > 1" class="pagination-controls">
          <button @click="goToPage(1)" :disabled="!pagination.has_prev">Primera</button>
          <button @click="prevPage" :disabled="!pagination.has_prev">Anterior</button>
          <span>Página {{ pagination.page }} de {{ pagination.total_pages }} ({{ pagination.total_items }} items)</span>
          <button @click="nextPage" :disabled="!pagination.has_next">Siguiente</button>
          <button @click="goToPage(pagination.total_pages)" :disabled="!pagination.has_next">Última</button>
        </div>
  
      </div>
       <!-- Show message if not superadmin or admin_datos not loaded yet -->
       <div v-else-if="!admin_datos || !admin_datos.superadmin" class="main-container">
         <div v-if="!admin_datos">Cargando datos de usuario...</div>
         <div v-else>Acceso no autorizado.</div>
      </div>
    </div>
  </div>
  </template>
  
  <script>
  import axios from 'axios';
  import { mapGetters } from 'vuex'; // Removed mapActions if not used here
  import SideNav from '@/components/SideNav.vue';
  import TopNav from '@/components/TopNav.vue';
  

  
  export default {
    name: 'ChatbotConversations',
    components: {
      SideNav,
      TopNav,
    },
    data() {
      return {
        loading: false, // Loading state for the main conversation list
        error: null, // Error message for the main list fetch
        conversations: [], // Array to hold conversation data from API
        pagination: null, // Object to hold pagination metadata
        currentPage: 1, // Current page number
        perPage: 20, // Items per page (match backend default or set)
        filterTelefono: '', // Input model for phone number filter
  
        // State for Solicitud expansion within a cell
        expandedSolicitudes: {}, // { convId: true/false }
  
        // NEW State for Row (Message) Expansion
        expandedRows: {}, // Tracks which conversation rows are expanded { convId: true/false }
        fetchedMessages: {}, // Stores messages fetched per conversation { convId: [...] }
        loadingMessages: {}, // Tracks loading state for messages per row { convId: true/false }
        errorMessages: {}, // Tracks errors for message fetching per row { convId: "error message" }
        // admin_datos is expected from Vuex via mapGetters
      }
    },
    computed: {
      // Ensure 'admin_datos' is available in your Vuex store and mapped
      ...mapGetters(['admin_datos']),
    },
    methods: {
      // --- Main Data Fetching ---
      async fetchConversations() {
        if (!this.admin_datos?.superadmin) {
            this.error = "Acceso no autorizado.";
            return; // Don't fetch if not superadmin
        }
        this.loading = true;
        this.error = null;
        // Reset expansion states when fetching new list data
        this.expandedRows = {};
        this.fetchedMessages = {};
        this.loadingMessages = {};
        this.errorMessages = {};
        this.expandedSolicitudes = {}; // Also reset solicitud expansion state
  
        const params = new URLSearchParams({
          page: this.currentPage,
          per_page: this.perPage,
        });
  
        if (this.filterTelefono) {
          params.append('telefono', this.filterTelefono.trim());
        }
  
        const apiUrl = `${window.hostname}/api/superadmin/chatbot_conversations?${params.toString()}`;
  
        try {
          // Assuming your auth token is handled by an Axios interceptor or globally
          const response = await axios.get(apiUrl);
          this.conversations = response.data.conversations; // Includes message_count now
          this.pagination = response.data.pagination;
          // Update currentPage from response in case the requested page was invalid
          this.currentPage = this.pagination.page;
        } catch (err) {
          console.error("Error fetching chatbot conversations:", err);
          this.error = 'Error al cargar las conversaciones. Por favor, inténtelo de nuevo.';
          // Handle specific errors (like 401 Unauthorized, 403 Forbidden) if needed
          if (err.response && (err.response.status === 401 || err.response.status === 403)) {
               this.error = 'Error de autenticación o autorización.';
               // maybe redirect to login: this.$router.push('/login');
          }
          this.conversations = []; // Clear data on error
          this.pagination = null;
        } finally {
          this.loading = false;
        }
      },
  
      // --- Methods for Row (Message) Expansion ---
      /**
       * Checks if the messages area for a given conversation row is expanded.
       * @param {number} conversationId
       * @returns {boolean}
       */
      isRowExpanded(conversationId) {
        // Use !! to cast to boolean, defaults to false if undefined
        return !!this.expandedRows[conversationId];
      },
  
      /**
       * Toggles the expanded state for a given conversation row.
       * Fetches messages if expanding for the first time.
       * Uses $set for Vue 2 reactivity.
       * @param {number} conversationId
       */
      toggleRow(conversationId) {
        const isCurrentlyExpanded = this.isRowExpanded(conversationId);
        // Use Vue.$set to ensure reactivity when adding/modifying object properties
        this.$set(this.expandedRows, conversationId, !isCurrentlyExpanded);
  
        // If we are now expanding (was previously collapsed) AND messages haven't been fetched yet
        if (!isCurrentlyExpanded && !this.fetchedMessages[conversationId]) {
          this.fetchMessagesForRow(conversationId);
        }
      },
  
      /**
       * Fetches messages for a specific conversation ID and updates state.
       * @param {number} conversationId
       */
      async fetchMessagesForRow(conversationId) {
        this.$set(this.loadingMessages, conversationId, true); // Set loading state for this row
        this.$set(this.errorMessages, conversationId, null); // Clear previous error for this row
  
        const apiUrl = `${window.hostname}/api/superadmin/chatbot_conversations/${conversationId}/messages`;
  
        try {
          const response = await axios.get(apiUrl);
          // Use $set to ensure reactivity when adding the message array to the fetchedMessages object
          this.$set(this.fetchedMessages, conversationId, response.data.messages);
        } catch (err) {
          console.error(`Error fetching messages for conv ${conversationId}:`, err);
          const errorMessage = err.response?.data?.message || err.message || 'Error desconocido.';
          this.$set(this.errorMessages, conversationId, errorMessage); // Store error for this row
           // Indicate fetch failed by setting messages to null or empty array
          this.$set(this.fetchedMessages, conversationId, null);
        } finally {
          this.$set(this.loadingMessages, conversationId, false); // Clear loading state for this row
        }
      },
  
      // --- Methods for Solicitud Expansion (within the cell) ---
       /**
       * Checks if the solicitudes list within a cell is expanded (showing all).
       * @param {number} conversationId
       * @returns {boolean}
       */
      isSolicitudesExpanded(conversationId) {
          return !!this.expandedSolicitudes[conversationId];
      },
  
      /**
       * Toggles the expanded state for the solicitudes list within a cell.
       * @param {number} conversationId
       */
      toggleSolicitudes(conversationId) {
          // Stop event propagation handled in template with @click.stop
          const currentState = this.isSolicitudesExpanded(conversationId);
          this.$set(this.expandedSolicitudes, conversationId, !currentState);
      },
  
      /**
       * Returns the array of solicitudes to display based on expansion state.
       * @param {object} conversation The conversation object from the loop
       * @returns {Array} Array of solicitud objects to display
       */
       getVisibleSolicitudes(conversation) {
          if (!conversation || !conversation.solicitudes_info) {
              return [];
          }
          const allSolicitudes = conversation.solicitudes_info;
          // If expanded or 2 or less items, show all
          if (this.isSolicitudesExpanded(conversation.id) || allSolicitudes.length <= 2) {
              return allSolicitudes;
          }
          // Otherwise, show only the first 2 (latest ones as per API sort)
          return allSolicitudes.slice(0, 2);
      },
  
      // --- Pagination Methods ---
      goToPage(pageNumber) {
        if (pageNumber >= 1 && this.pagination && pageNumber <= this.pagination.total_pages && pageNumber !== this.currentPage) {
          this.currentPage = pageNumber;
          this.fetchConversations(); // Fetch new page data
        }
      },
      nextPage() {
        if (this.pagination && this.pagination.has_next) {
          this.goToPage(this.currentPage + 1);
        }
      },
      prevPage() {
        if (this.pagination && this.pagination.has_prev) {
          this.goToPage(this.currentPage - 1);
        }
      },
  
      // --- Filter Methods ---
      applyFilter() {
          this.currentPage = 1; // Reset to first page when filter changes
          this.fetchConversations(); // Fetch filtered data
      },
      clearFilter() {
          this.filterTelefono = '';
          this.applyFilter(); // Fetch again without the filter
      },
  
      // --- Formatting Helpers ---
      /**
       * Formats an ISO date string into a readable format.
       * @param {string} dateString ISO date string
       * @param {boolean} showTime Whether to include the time
       * @returns {string} Formatted date/time string or 'N/A'
       */
      formatDate(dateString, showTime = false) {
        if (!dateString) return 'N/A';
        try {
          const options = { year: 'numeric', month: 'short', day: 'numeric' };
          if (showTime) {
              options.hour = '2-digit';
              options.minute = '2-digit';
              options.second = '2-digit'; // Optional: add seconds
          }
          // Use 'es-CL' locale for Chilean Spanish formatting
          return new Date(dateString).toLocaleDateString('es-CL', options);
        } catch (e) {
          console.error("Error formatting date:", dateString, e);
          return dateString; // Return original string if formatting fails
        }
      },
  
      /**
       * Formats the conversation state integer into a readable string.
       * Replace with your actual state mappings.
       * @param {number} estado Conversation state integer
       * @returns {string} Human-readable state
       */
      formatEstado(estado) {
          // Example mapping (replace with your actual states)
          const map = {
              0: 'Inicio/Reset', 1: 'Menú Principal', 2: 'Pidiendo Nombre',
              3: 'Pidiendo Comuna', 4: 'Confirmando Comuna', 5: 'Pidiendo Categoría',
              6: 'Pidiendo Descripción', 7: 'Esperando Fotos', 8: 'Pidiendo Email',
              11: 'FAQ', 91: 'Rescatado'
          };
          return map[estado] || `Estado ${estado}` || 'N/A';
      },
  
      /**
       * Formats the SolicitudCliente state integer into a readable string.
       * Replace with your actual state mappings.
       * @param {number} estado Solicitud state integer
       * @returns {string} Human-readable state
       */
      formatSolicitudEstado(estado) {
          // Example mapping (replace with your actual states)
          const map = { 0: 'Nuevo', 1: 'Asignado', 5:'En Proceso', 10: 'Completado', 99: 'Cancelado' };
          return map[estado] || `Estado Sol. ${estado}` || 'N/A';
      }
    },
    // --- Lifecycle Hooks ---
    mounted() {
      // Initial check and fetch if authorized
      if (this.admin_datos && this.admin_datos.superadmin) {
        this.fetchConversations();
      }
      // Watcher below handles async loading of admin_datos
    },
    watch: {
       // Watch admin_datos in case it loads after mount
       admin_datos(newVal) {
         if (newVal && newVal.superadmin && !this.loading && this.conversations.length === 0 && !this.error) {
           // If user data loaded, user is superadmin, and we haven't loaded data yet or errored
           console.log("Admin data loaded, fetching conversations...");
           this.fetchConversations();
         } else if (newVal && !newVal.superadmin) {
           console.log("Redirecting: User loaded, not superadmin.");
           // Optionally redirect if needed: this.$router.push('/');
         }
       }
    }
  }
  </script>
  
  <style scoped>
  /* Import or reuse styles from your example */
  .column-right{
    background-color: rgb(242, 247, 251);
    padding-top:80px;
    padding-left:260px; /* Adjust if SideNav width changes */
    width:100%;
    box-sizing: border-box; /* Include padding in width calculation */
    min-height: 100vh; /* Ensure it takes full height */
  }
  .main-container{
    background-color: white;
    margin: 10px 20px; /* Add some margin */
    padding: 20px;
    border-radius: 8px; /* Optional: adds rounded corners */
    box-shadow: 0 2px 4px rgba(0,0,0,0.1); /* Optional: adds subtle shadow */
  }
  
  h2 {
      margin-bottom: 15px;
      color: #333;
  }
  
  hr {
      margin-bottom: 20px;
      border: 0;
      border-top: 1px solid #eee;
  }
  
  /* Filter Section Styles */
  .filter-section {
      margin-bottom: 20px;
      padding: 15px;
      background-color: #f8f9fa;
      border: 1px solid #dee2e6;
      border-radius: 4px;
      display: flex;
      align-items: center;
      flex-wrap: wrap; /* Allow wrapping on smaller screens */
  }
  .filter-section label {
      margin-right: 10px;
      font-weight: bold;
      margin-bottom: 5px; /* Spacing for wrapping */
  }
  .filter-section input[type="text"] {
      padding: 8px 12px;
      border: 1px solid #ced4da;
      border-radius: 4px;
      margin-right: 5px;
      margin-bottom: 5px; /* Spacing for wrapping */
      flex-grow: 1; /* Allow input to take space */
      min-width: 150px;
  }
  .filter-section button {
      padding: 8px 15px;
      background-color: #007bff;
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      transition: background-color 0.2s;
      margin-bottom: 5px; /* Spacing for wrapping */
  }
  .filter-section button:hover:not(:disabled) {
      background-color: #0056b3;
  }
  .filter-section button:disabled {
      background-color: #cccccc;
      cursor: not-allowed;
  }
  
  /* Loading / Error / No Data Styles */
  .loading-indicator, .error-message {
    text-align: center;
    padding: 20px;
    font-style: italic;
    color: #666;
  }
  .error-message {
    color: #dc3545; /* Bootstrap danger color */
    font-weight: bold;
    background-color: #f8d7da;
    border: 1px solid #f5c6cb;
    border-radius: 4px;
    padding: 10px 15px;
  }
  
  /* Table Styles */
  .data-table-container {
      overflow-x: auto; /* Allows table to scroll horizontally on small screens */
  }
  .conversation-table {
      border-collapse: separate; /* Use separate for spacing between expanded rows */
      border-spacing: 0; /* Reset default spacing */
      width: 100%;
      margin-top: 15px;
      font-size: 0.9em;
      box-shadow: 0 0 5px rgba(0,0,0,0.05);
  }
  th, td {
    border: 1px solid #ddd;
    padding: 10px 12px;
    text-align: left;
    vertical-align: middle; /* Align text vertically */
  }
  th {
    background-color: #f2f2f2;
    font-weight: bold;
    white-space: nowrap; /* Prevent headers from wrapping */
    position: sticky; /* Make header sticky on scroll if needed */
    top: 0; /* Required for sticky header */
    z-index: 1;
  }
  /* Zebra striping for main rows */
  .conversation-row:nth-child(4n-1), /* Adjust selectors based on template structure */
  .conversation-row:nth-child(4n) {
    /* background-color: #f9f9f9; */ /* Can conflict with hover/expanded, remove if needed */
  }
  
  /* Main Row Interaction Styles */
  .conversation-row {
      cursor: pointer;
      transition: background-color 0.2s ease;
  }
  .conversation-row:hover {
      background-color: #f5f5f5; /* Light hover effect */
  }
  .conversation-row.expanded {
      background-color: #e9ecef; /* Slightly darker when expanded */
      font-weight: bold; /* Make main row bold when expanded */
      border-bottom: none; /* Remove bottom border when expanded */
  }
  .expander-icon {
      display: inline-block;
      width: 20px; /* Ensure consistent width */
      text-align: center;
      font-weight: bold;
      color: #6c757d;
  }
  
  /* Expanded Message Row Styles */
  .messages-row td {
      padding: 0; /* Remove padding from the TD */
      border: none; /* Remove default borders for this special row */
      /* Border is applied by messages-container instead */
  }
  .messages-container {
      padding: 15px 20px; /* Add padding inside the container */
      background-color: #ffffff; /* White background for message area */
      border: 1px solid #dee2e6; /* Border around the container */
      border-top: none; /* No top border as it's connected to the main row */
      /* border-left: 3px solid #6c757d; /* Left accent border */
  }
  
  /* Message List Styles */
  .message-list {
      list-style: none;
      padding: 0;
      margin: 0;
      max-height: 400px; /* Limit height and make scrollable */
      overflow-y: auto;
      /* border: 1px solid #eee; */ /* Container has border now */
      border-radius: 4px;
  }
  .message-item {
      padding: 10px 15px;
      margin-bottom: 8px;
      border-radius: 8px;
      line-height: 1.4;
      max-width: 85%; /* Limit message width */
      word-wrap: break-word; /* Wrap long words */
      border: 1px solid #eee; /* Light border around each message */
  }
  .message-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 5px;
      font-size: 0.8em;
      color: #555;
  }
  .message-origin {
      font-weight: bold;
      text-transform: capitalize;
  }
  .message-timestamp {
      font-style: italic;
      color: #777;
  }
  .message-content pre {
      margin: 0; /* Reset pre margin */
      white-space: pre-wrap; /* Allow wrapping within pre */
      font-family: inherit; /* Use table font */
      font-size: 0.95em;
      color: #333;
  }
  
  /* User vs Bot Message Styling */
  .message-usuario {
      background-color: #d1e7ff; /* Lighter blue background for user */
      margin-left: auto; /* Align user messages to the right */
      border-bottom-right-radius: 0; /* Bubble effect */
      border-color: #b6d4fe;
  }
  .message-bot {
      background-color: #f8f9fa; /* Lighter grey background for bot */
      margin-right: auto; /* Align bot messages to the left */
      border-bottom-left-radius: 0; /* Bubble effect */
      border-color: #e9ecef;
  }
  
  /* Solicitud Styles (within cell) */
  .solicitudes-cell {
      font-size: 0.85em; /* Make text slightly smaller */
      vertical-align: top; /* Align content to top */
  }
  .solicitud-item {
      padding: 5px;
      border: 1px dashed #eee; /* Light border for separation */
      border-radius: 4px;
      background-color: #fafafa; /* Slightly different background */
  }
  .solicitud-item div {
      margin-bottom: 2px; /* Small space between lines */
      line-height: 1.3; /* Adjust line height */
  }
  .solicitud-item strong {
      color: #555; /* Darker grey for labels */
  }
  .mt-10 { /* Add margin-top to separate multiple items within the same cell */
      margin-top: 10px;
  }
  .show-more-less-btn { /* Style for the Solicitud show/hide button */
      background: none;
      border: none;
      color: #007bff; /* Link color */
      cursor: pointer;
      padding: 5px 0;
      margin-top: 5px;
      font-size: 0.9em;
      text-align: left;
      display: block;
  }
  .show-more-less-btn:hover {
      text-decoration: underline;
  }
  
  /* Boolean Status Styles */
  .status-yes {
      color: #198754; /* Bootstrap success green */
      font-weight: bold;
  }
  .status-no {
      color: #dc3545; /* Bootstrap danger red */
      font-weight: bold;
  }
  
  /* Pagination Styles */
  .pagination-controls {
    margin-top: 20px;
    text-align: center;
  }
  .pagination-controls button {
    padding: 8px 12px;
    margin: 0 5px;
    background-color: #6c757d;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.2s;
  }
  .pagination-controls button:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
  .pagination-controls button:hover:not(:disabled) {
    background-color: #5a6268;
  }
  .pagination-controls span {
    margin: 0 10px;
    vertical-align: middle;
  }
  
  /* Responsive Adjustments */
  @media (max-width: 800px) {
   .column-right{
    padding-left:0px;
   }
   .main-container {
       margin: 10px; /* Reduce margin on smaller screens */
   }
   .filter-section input[type="text"] {
       /* width: calc(100% - 150px); /* Adjust input width if needed */
       min-width: 120px; /* Adjust min-width */
   }
   .message-item {
       max-width: 95%; /* Allow messages to take more width on small screens */
   }
  }
  </style>